import { useState } from 'react';
import axios from 'axios';
import { WinRadio, WinCheckbox } from './WinComponents';
import DateInput from './DateInput';
import { formatNomPropre, formatNAS, formatCodePostal, formatTelephone, formatAdresse, calculerAge } from '../utils/formatters';
import VilleAutocomplete from './VilleAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConjointTab = ({
  formData, updateConjoint, provinces,
  handleMemeAdresseConjoint, handleVerifInteretsOpposes, handleSupprimerConjoint,
  noseqdaj, showPopup, onArt67Applied, clearConjointRevenus,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  // Modal de confirmation pour le changement d'article 7(1)/7(2) — équivalent
  // du MsgBox YesNo dans MDIParent2.vb:5992 ("Les informations financières du
  // conjoint déjà enregistrées seront supprimées..."). `pendingArt` contient
  // la valeur "7(1)" ou "7(2)" en attente de confirmation.
  const [pendingArt, setPendingArt] = useState(null);
  const [art67Busy, setArt67Busy] = useState(false);

  const art67Value = formData.conjoint.art_6_ou_7 || '';

  // Equivalent du handler cboArt6ou7Conj_DropDownClosed (MDIParent2.vb:5989-6020).
  // Sélection vide -> reset direct (jamais de prompt). Sinon, demande de
  // confirmation puis (si Oui) suppression des infofin sourceid='C' et
  // application de l'état chkIntOppose dérivé.
  const handleArt67Change = (newValue) => {
    if (!newValue) {
      // Reset direct (équivalent au cboArt6ou7Conj.Text = "" du legacy).
      updateConjoint('art_6_ou_7', '');
      updateConjoint('interets_opposes', false);
      return;
    }
    if (newValue === art67Value) return;
    setPendingArt(newValue);
  };

  const confirmArt67 = async () => {
    if (!pendingArt) return;
    setArt67Busy(true);
    try {
      // Equivalent SuppToutInfoFin("C") : DELETE infofin WHERE sourceid='C'
      // AND noseqdaj=? . Si pas de dossier sauvegardé encore (noseqdaj vide),
      // on saute l'appel API — il n'y a rien en BD à supprimer.
      if (noseqdaj) {
        try {
          await axios.delete(`${API}/dossier/${encodeURIComponent(noseqdaj)}/infofin/conjoint`);
        } catch (err) {
          // Erreur réseau/serveur : on log et on continue à appliquer
          // l'état UI (le save-dossier final écrasera adda248 correctement).
          console.error('Erreur DELETE infofin conjoint:', err);
        }
      }
      // Applique l'état dérivé exact de MDIParent2.vb:5994-6009 :
      //   7(1) -> chkIntOppose coché + libellé "Intérêt opposé"
      //   7(2) -> chkIntOppose décoché + libellé "Au bénéfice enfant"
      updateConjoint('art_6_ou_7', pendingArt);
      updateConjoint('interets_opposes', pendingArt === '7(1)');
      // Equivalent de MisJrCal(True) (MDIParent2.vb:6011) : remettre à zéro
      // tous les revenus consolidés du conjoint (adda320-adda338) puisque
      // leur source — les lignes infofin sourceid='C' — vient d'être effacée.
      if (typeof clearConjointRevenus === 'function') {
        clearConjointRevenus();
      }
      if (typeof onArt67Applied === 'function') {
        // Permet au parent de recalculer les totaux (équivalent MisJrCal(True)).
        onArt67Applied(pendingArt);
      }
    } finally {
      setArt67Busy(false);
      setPendingArt(null);
    }
  };

  const cancelArt67 = () => {
    // Equivalent legacy MDIParent2.vb:6019 : `cboArt6ou7Conj.Text = ""`.
    // Le legacy reset TOUJOURS à vide en cas de Non, même si l'utilisateur
    // avait précédemment sélectionné un autre art.7. On force donc le reset
    // dans le state global (pas seulement le pendingArt).
    updateConjoint('art_6_ou_7', '');
    updateConjoint('interets_opposes', false);
    setPendingArt(null);
  };

  // Etat du checkbox chkIntOppose dérivé exactement comme le legacy
  // (MDIParent2.vb:5994-6009) :
  //   art_6_ou_7 = "7(1)" -> coché, désactivé, libellé "Intérêt opposé"
  //   art_6_ou_7 = "7(2)" -> décoché, désactivé, libellé "Au bénéfice enfant"
  //   sinon (vide)         -> état libre, activé, libellé "Intérêt opposé"
  const chkLabel = art67Value === '7(2)' ? "Au bénéfice de l'enfant" : 'Intérêt opposé';
  const chkDisabled = art67Value === '7(1)' || art67Value === '7(2)';
  const chkChecked = art67Value === '7(1)' ? true : (art67Value === '7(2)' ? false : !!formData.conjoint.interets_opposes);

  return (
  <div className="win-tab-content compact">
    <div className="win-two-columns">
      <fieldset className="win-groupbox compact">
        <legend>Identification du conjoint</legend>
        <div className="win-compact-grid-2">
          <div className="win-field">
            <label className="win-field-label">Nom</label>
            <input className="win-input" value={formData.conjoint.nom} onChange={e => updateConjoint('nom', formatNomPropre(e.target.value))} data-testid="input-nom-conjoint" maxLength={40} />
          </div>
          <div className="win-field">
            <label className="win-field-label">Prénom</label>
            <input className="win-input" value={formData.conjoint.prenom} onChange={e => updateConjoint('prenom', formatNomPropre(e.target.value))} data-testid="input-prenom-conjoint" maxLength={40} />
          </div>
        </div>
        <div className="win-compact-grid-2" style={{marginTop: 8}}>
          <div style={{display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12}}>
            <div className="win-field">
              <label className="win-field-label">Date naissance</label>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <div style={{width: 120, flexShrink: 0}}>
                  <DateInput value={formData.conjoint.date_naissance} onChange={v => updateConjoint('date_naissance', v)} testId="input-datenaissance-conjoint" />
                </div>
                {calculerAge(formData.conjoint.date_naissance) !== null && <span style={{fontSize: 12, color: '#555', whiteSpace: 'nowrap', fontWeight: 'bold'}}>{calculerAge(formData.conjoint.date_naissance)} ans</span>}
              </div>
            </div>
            <div className="win-field">
              <label className="win-field-label">Sexe</label>
              <div className="win-radio-group">
                <WinRadio name="sexe_conj" value="0" checked={formData.conjoint.sexe === '0'} onChange={v => updateConjoint('sexe', v)} label="F" />
                <WinRadio name="sexe_conj" value="1" checked={formData.conjoint.sexe === '1'} onChange={v => updateConjoint('sexe', v)} label="M" />
              </div>
            </div>
          </div>
          <div className="win-field">
            <label className="win-field-label">N.A.S.</label>
            <input className="win-input" value={formData.conjoint.nas} onChange={e => updateConjoint('nas', formatNAS(e.target.value))} placeholder="000-000-000" maxLength={11} data-testid="input-nas-conjoint" style={{width: '140px'}} />
          </div>
        </div>
      </fieldset>

      <fieldset className="win-groupbox compact">
        <legend style={{display: 'flex', alignItems: 'center', gap: 8}}>
          Coordonnées du conjoint
          <button
            className="win-btn"
            type="button"
            title="Copier l'adresse du requérant"
            data-testid="btn-meme-adresse-conjoint"
            style={{padding: '2px 5px', lineHeight: 1, minWidth: 'unset'}}
            onClick={handleMemeAdresseConjoint}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>
          </button>
        </legend>
        <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr 90px 70px', gap: 12}}>
          <div className="win-field">
            <label className="win-field-label">Adresse</label>
            <input className="win-input" value={formData.conjoint.adresse} onChange={e => updateConjoint('adresse', formatAdresse(e.target.value))} maxLength={40} />
          </div>
          <div className="win-field">
            <label className="win-field-label">Ville</label>
            <VilleAutocomplete value={formData.conjoint.ville} onChange={v => updateConjoint('ville', formatNomPropre(v))} maxLength={40} testId="input-conjoint-ville" />
          </div>
          <div className="win-field">
            <label className="win-field-label">Code postal</label>
            <input className="win-input" value={formData.conjoint.code_postal} onChange={e => updateConjoint('code_postal', formatCodePostal(e.target.value))} maxLength={7} placeholder="A1A 1A1" />
          </div>
          <div className="win-field">
            <label className="win-field-label">Prov.</label>
            <select className="win-select" value={formData.conjoint.province} onChange={e => updateConjoint('province', e.target.value)} style={{width: '100%', minWidth: 'unset'}}>
              {provinces.map(p => <option key={p.value} value={p.value}>{p.value}</option>)}
            </select>
          </div>
        </div>
        <div style={{display: 'grid', gridTemplateColumns: '120px 120px 65px 1fr', gap: 8, marginTop: 8}}>
          <div className="win-field">
            <label className="win-field-label">Tél. résidence</label>
            <input className="win-input" value={formData.conjoint.telephone_residence} onChange={e => updateConjoint('telephone_residence', formatTelephone(e.target.value))} maxLength={12} placeholder="514-555-1234" />
          </div>
          <div className="win-field">
            <label className="win-field-label">Tél. bureau</label>
            <input className="win-input" value={formData.conjoint.telephone_bureau} onChange={e => updateConjoint('telephone_bureau', formatTelephone(e.target.value))} maxLength={12} placeholder="514-555-1234" />
          </div>
          <div className="win-field">
            <label className="win-field-label">Ext</label>
            <input className="win-input" value={formData.conjoint.telephone_ext} onChange={e => updateConjoint('telephone_ext', e.target.value.replace(/\D/g, '').slice(0, 5))} maxLength={5} placeholder="00000" />
          </div>
          <div className="win-field">
            <label className="win-field-label">Courriel</label>
            <input className="win-input" type="email" value={formData.conjoint.courriel} onChange={e => updateConjoint('courriel', e.target.value.toLowerCase())} maxLength={100} />
          </div>
        </div>
      </fieldset>
    </div>

    <fieldset className="win-groupbox compact">
      <legend>Informations administratives</legend>
      <div style={{display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'}}>
        <div className="win-info-icon" data-tooltip={"7. Malgré l'article 6.1, l'admissibilité financière d'un requérant est établie en ne prenant pas en considération les revenus et les actifs de son conjoint lorsque:\n\n1 dans une affaire ou un recours, ils ont des intérêts opposés;\n\n2 le requérant présente une demande d'aide juridique pour le bénéfice d'un enfant mineur dont il a la garde en tant que père ou mère ou parent ou, selon le cas, en tant que personne visée à l'article 2."}>
          ℹ️
        </div>
        <label className="win-field-label" style={{marginBottom: 0}}>art. 7(2) ou 7(1)</label>
        <select
          className="win-select"
          value={art67Value}
          onChange={e => handleArt67Change(e.target.value)}
          style={{width: '80px'}}
          disabled={art67Busy}
          data-testid="select-art-6-ou-7-conjoint"
        >
          <option value=""></option>
          <option value="7(1)">7(1)</option>
          <option value="7(2)">7(2)</option>
        </select>
        {/* chkIntOppose : panneau visible uniquement quand l'art.7 est actif
            (équivalent PanelIntOppose.Visible dans MDIParent2.vb:5995/6007). */}
        {(art67Value === '7(1)' || art67Value === '7(2)') && (
          <div
            style={{display: 'flex', alignItems: 'center', gap: 6}}
            data-testid="panel-int-oppose-conjoint"
          >
            <input
              type="checkbox"
              checked={chkChecked}
              disabled={chkDisabled}
              readOnly
              data-testid="chk-int-oppose-conjoint"
            />
            <span
              className={art67Value === '7(1)' ? 'win-art7-label win-art7-interet' : 'win-art7-label win-art7-benefice'}
              data-testid="label-int-oppose-conjoint"
            >
              {chkLabel}
            </span>
          </div>
        )}
      </div>
    </fieldset>

    <fieldset className="win-groupbox compact">
      <legend>Actions</legend>
      <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
        <button className="win-btn-action" onClick={handleVerifInteretsOpposes} data-testid="btn-verif-interets-opposes">
          Vérif. intérêts opposés
        </button>
        <button className="win-btn-action win-btn-danger" onClick={() => setShowConfirm(true)} data-testid="btn-supprimer-conjoint">
          Supprimer le conjoint
        </button>
      </div>
    </fieldset>

    {showConfirm && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 3000
      }}>
        <div style={{
          backgroundColor: '#f0f0f0', border: '2px solid #333',
          borderRadius: 0, width: '400px',
          boxShadow: '4px 4px 10px rgba(0,0,0,0.3)'
        }}>
          <div style={{
            backgroundColor: '#c03030', color: 'white',
            padding: '8px 14px', fontWeight: 'bold', fontSize: '14px',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <span style={{fontSize: '18px'}}>&#9888;&#65039;</span>
            ATTENTION
          </div>
          <div style={{padding: '24px'}}>
            <p style={{margin: '0 0 24px 0', fontSize: '14px', textAlign: 'center', color: '#333'}}>
              Voulez-vous supprimer le conjoint?
            </p>
            <div style={{display: 'flex', justifyContent: 'center', gap: 20}}>
              <button
                className="win-btn"
                onClick={() => { setShowConfirm(false); handleSupprimerConjoint(); }}
                style={{padding: '8px 24px', fontWeight: 'bold', fontSize: '13px', minWidth: '80px'}}
                data-testid="btn-confirm-supprimer-oui"
              >
                Oui
              </button>
              <button
                className="win-btn"
                onClick={() => setShowConfirm(false)}
                style={{padding: '8px 24px', fontSize: '13px', minWidth: '80px'}}
                data-testid="btn-confirm-supprimer-non"
              >
                Non
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    {pendingArt && (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 3000
        }}
        data-testid="modal-confirm-art67"
      >
        <div style={{
          backgroundColor: '#f0f0f0', border: '2px solid #333',
          borderRadius: 0, width: '460px',
          boxShadow: '4px 4px 10px rgba(0,0,0,0.3)'
        }}>
          <div style={{
            backgroundColor: '#c03030', color: 'white',
            padding: '8px 14px', fontWeight: 'bold', fontSize: '14px',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <span style={{fontSize: '18px'}}>&#9888;&#65039;</span>
            Avis
          </div>
          <div style={{padding: '24px'}}>
            <p style={{margin: '0 0 24px 0', fontSize: '14px', textAlign: 'center', color: '#333'}}>
              {`Les informations financières du conjoint déjà enregistrées seront supprimées à cause de l'article ${pendingArt}.`}
            </p>
            <div style={{display: 'flex', justifyContent: 'center', gap: 20}}>
              <button
                className="win-btn"
                onClick={confirmArt67}
                disabled={art67Busy}
                style={{padding: '8px 24px', fontWeight: 'bold', fontSize: '13px', minWidth: '80px'}}
                data-testid="btn-confirm-art67-oui"
              >
                Oui
              </button>
              <button
                className="win-btn"
                onClick={cancelArt67}
                disabled={art67Busy}
                style={{padding: '8px 24px', fontSize: '13px', minWidth: '80px'}}
                data-testid="btn-confirm-art67-non"
              >
                Non
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

  </div>
  );
};

export default ConjointTab;
